import { useEffect, useState } from 'react'
import { api } from '../api/client'
import type { Me } from '../api/types'
import { radius } from '../components/primitives/shape'

/**
 * Read `?password_token=` once, then take it out of the address bar.
 *
 * The same idiom as `consumeAuthError` in AuthPanel, and here it is not merely
 * tidy: a credential left in the URL ends up in browser history, in a bookmark
 * if they save the page, and in the `Referer` header of anything the page
 * loads afterwards.
 */
export function consumePasswordToken(): string | null {
  if (typeof window === 'undefined') return null
  const found = new URLSearchParams(window.location.search).get('password_token')
  if (!found) return null

  const url = new URL(window.location.href)
  url.searchParams.delete('password_token')
  window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`)

  return found
}

/**
 * The screen an emailed password link opens.
 *
 * One screen for two doors — somebody who forgot a password, and somebody
 * invited who never had one. It does not know which, and does not need to:
 * "Choose a password" is true either way, which is why the token carries no
 * purpose.
 */
export function SetPassword({
  token,
  onSignedIn,
}: {
  token: string
  onSignedIn: (me: Me) => void
}) {
  const [state, setState] = useState<'checking' | 'ready' | 'stale'>('checking')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    let live = true
    api
      .passwordTokenEmail({ token })
      .then((found) => {
        if (!live) return
        setEmail(found.email)
        setState('ready')
      })
      .catch(() => {
        if (live) setState('stale')
      })
    return () => {
      live = false
    }
  }, [token])

  async function submit(event: React.FormEvent) {
    event.preventDefault()
    setBusy(true)
    setError(null)
    try {
      onSignedIn(await api.resetPassword({ token, password }))
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : 'that did not work')
      setBusy(false)
    }
  }

  return (
    <div style={{ maxWidth: 420, margin: '80px auto', padding: '0 20px' }}>
      <h1 style={{ margin: '0 0 6px', fontSize: 20, fontWeight: 800, letterSpacing: '-.02em' }}>
        Choose a password
      </h1>

      {state === 'checking' && (
        <p role="status" style={{ fontSize: 13, color: 'var(--text-dim)' }}>
          Checking that link…
        </p>
      )}

      {/* Not an error. This is the most likely way anybody gets here. */}
      {state === 'stale' && (
        <>
          <p role="status" style={{ fontSize: 13.5, lineHeight: 1.6, color: 'var(--text-dim)' }}>
            That link is no longer good. They last an hour and can only be used once.
          </p>
          <button
            type="button"
            onClick={() => window.location.assign('/')}
            style={{ marginTop: 14, fontSize: 13, color: 'var(--accent)', cursor: 'pointer', padding: 0 }}
          >
            Back to sign in
          </button>
        </>
      )}

      {state === 'ready' && (
        <form onSubmit={submit} style={{ display: 'grid', gap: 10, marginTop: 14 }}>
          <p style={{ margin: 0, fontSize: 13, lineHeight: 1.6, color: 'var(--text-dim)' }}>
            For {email}.
          </p>
          <input
            type="password"
            required
            value={password}
            aria-label="New password"
            placeholder="at least 10 characters"
            onChange={(event) => setPassword(event.target.value)}
            style={{
              fontSize: 13.5,
              color: 'var(--text)',
              background: 'var(--panel-strong)',
              border: '1px solid var(--border-strong)',
              borderRadius: radius.sm,
              padding: '11px 14px',
            }}
          />
          {error && (
            <p role="alert" style={{ margin: 0, fontSize: 12.5, color: 'var(--deletions)' }}>
              {error}
            </p>
          )}
          <button
            type="submit"
            disabled={busy}
            style={{
              padding: '13px 18px',
              borderRadius: radius.action,
              background: 'var(--accent)',
              color: 'var(--ground)',
              fontSize: 14.5,
              fontWeight: 700,
              cursor: 'pointer',
              opacity: busy ? 0.6 : 1,
            }}
          >
            Set password
          </button>
          <p style={{ margin: 0, fontSize: 11.5, lineHeight: 1.55, color: 'var(--text-meta)' }}>
            This signs you out everywhere else. Your machines keep their access.
          </p>
        </form>
      )}
    </div>
  )
}
